import { AnimatePresence } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { AddToCollectionModal } from "./AddToCollectionModal";
import {
  type CollectionImageTransition,
  FavoriteCollectionPicker,
  type PopoverAnchorLocation,
} from "./FavoriteCollectionPicker";
import { FavoriteCollectionToast } from "./FavoriteCollectionToast";
import { FavoriteSavedToast } from "./FavoriteSavedToast";
import {
  addProductToCollection,
  ALL_FAVORITES_COLLECTION_ID,
  getFirstCollectionImageUrl,
  getProductImageUrl,
  INITIAL_ALL_FAVORITES_IMAGE_URL,
  INITIAL_COLLECTIONS,
  removeProductFromCollection,
  setCollections,
  setProductTokensByCollection,
  updateAllFavoritesCollectionImage,
  useCollections,
  useProductTokensByCollection,
} from "./savedStore";

export const EXTRA_COLLECTIONS_HIDDEN = 1;
export const EXTRA_COLLECTIONS_VISIBLE = 2;

// Post-save surface variants (pressure-testing how upfront organization is).
export const SAVE_FLOW_POPOVER = 1;
export const SAVE_FLOW_EXPANDING_TOAST = 2;
export const SAVE_FLOW_CONFIRMATION_TOAST = 3;
export const SAVE_FLOW_POPOVER_CONDENSED = 4;
export const SAVE_FLOW_TOAST_BLACK = 5;
export const SAVE_FLOW_TOAST_LIGHT = 6;
export const SAVE_FLOW_POPOVER_CONDENSED_R2 = 7;
export const SAVE_FLOW_POPOVER_R2 = 8;
export const SAVE_FLOW_TOAST_DARK_BOTTOM = 9;
export const SAVE_FLOW_TOAST_DARK_BOTTOM_LIGHT_EXPAND = 10;
// Condensed R2 + explicit confirm: a fresh save still auto-dismisses, but any
// collection add/remove stages the change and shows a "Save" button instead.
export const SAVE_FLOW_POPOVER_CONDENSED_R3 = 11;
// Bottom-aligned toast + explicit confirm: collection add/remove is staged and
// committed with a "Save" button instead of applying on each toggle.
export const SAVE_FLOW_EXPANDING_TOAST_R2 = 12;

const POPOVER_ANCHOR_PRODUCT_CARD = 1;
const POPOVER_ANCHOR_HEART = 2;
const CLOSE_AFTER_SELECTION_OFF = 1;
const CLOSE_AFTER_SELECTION_ON = 2;

type CollectionImageTransitions = Record<string, CollectionImageTransition>;
type PopoverAnchorLocationState =
  | typeof POPOVER_ANCHOR_PRODUCT_CARD
  | typeof POPOVER_ANCHOR_HEART;

const SEEDED_EXTRA_COLLECTION_IDS = new Set(
  INITIAL_COLLECTIONS.filter(
    (collection) => collection.id !== ALL_FAVORITES_COLLECTION_ID
  ).map((collection) => collection.id)
);

let collectionImagesReadyPromise: Promise<void> | undefined;

const preloadCollectionImages = (): Promise<void> => {
  collectionImagesReadyPromise ??= Promise.all(
    INITIAL_COLLECTIONS.map(async (collection) => {
      if (!collection.imageUrl) {
        return;
      }
      const image = new Image();
      image.src = collection.imageUrl;
      await image.decode().catch(() => undefined);
    })
  ).then(() => undefined);
  return collectionImagesReadyPromise;
};

export interface UseFavoritesControllerOptions {
  /** Which post-save surface appears when a product is saved. */
  saveFlowVariant: number;
}

export interface FavoritesController {
  handleFavoriteClick: (
    productToken: string,
    isFavorited: boolean,
    anchorElement: HTMLElement,
    productImageUrl?: string
  ) => void;
  isSaved: (productToken: string) => boolean;
  /** Token of the product whose post-save surface (popover or collection
   * toast) is currently open, or null. Drives the heart's save animation hold. */
  organizingProductToken: string | null;
  savedCount: number;
  extraCollectionsState: number;
  setExtraCollectionsState: React.Dispatch<React.SetStateAction<number>>;
  popoverElement: React.ReactNode;
  toastElement: React.ReactNode;
  collectionToastElement: React.ReactNode;
}

export const useFavoritesController = ({
  saveFlowVariant,
}: UseFavoritesControllerOptions): FavoritesController => {
  const collections = useCollections();
  const productTokensByCollection = useProductTokensByCollection();

  const activeProductTokenRef = useRef<string | null>(null);
  // Image overrides for tokens that aren't entity-backed (e.g. cart line items
  // keyed by a synthetic id), so the picker/toast can still show an image.
  const productImageOverrides = useRef<Record<string, string>>({});
  const isPickerOpenRef = useRef(false);
  const isCollectionToastOpenRef = useRef(false);
  const nextCollectionNumber = useRef(1);
  const pendingAllFavoritesImageRef = useRef<{
    imageUrl: string | undefined;
  } | null>(null);
  const [activeProductToken, setActiveProductToken] = useState<string | null>(
    null
  );
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isPickerRendered, setIsPickerRendered] = useState(false);
  const [isCollectionToastOpen, setIsCollectionToastOpen] = useState(false);
  const [isAddToCollectionModalOpen, setIsAddToCollectionModalOpen] =
    useState(false);
  const [collectionToastInitialExpanded, setCollectionToastInitialExpanded] =
    useState(false);
  const [pickerInitialExpanded, setPickerInitialExpanded] = useState(false);
  const [extraCollectionsState, setExtraCollectionsState] = useState(
    EXTRA_COLLECTIONS_VISIBLE
  );
  // Locked-in favoriting behavior (toggles removed): the popover always anchors
  // to the heart, always closes after a selection, and the first heart click
  // always opens the collections picker.
  const popoverAnchorLocation: PopoverAnchorLocationState =
    POPOVER_ANCHOR_HEART;
  const closeAfterSelectionState = CLOSE_AFTER_SELECTION_ON;
  const [collectionImageTransitions, setCollectionImageTransitions] =
    useState<CollectionImageTransitions>({});
  const [savedProductToast, setSavedProductToast] = useState<
    | { kind: "created-collection"; collectionName: string; imageUrl: string }
    | { kind: "saved-to-favorites"; imageUrl: string }
    | null
  >(null);

  useEffect(() => {
    void preloadCollectionImages();
  }, []);

  useEffect(() => {
    if (!savedProductToast) {
      return;
    }
    const timeout = window.setTimeout(() => setSavedProductToast(null), 5000);
    return () => window.clearTimeout(timeout);
  }, [savedProductToast]);

  const resolveImage = useCallback(
    (productToken: string): string | undefined =>
      getProductImageUrl(productToken) ??
      productImageOverrides.current[productToken],
    []
  );

  const handlePickerClose = useCallback(() => {
    isPickerOpenRef.current = false;
    setIsPickerOpen(false);
  }, []);

  // Opens the centered "Add to collection" modal for the already-active
  // product — used by the confirmation toast's "Add to collection" action.
  const openAddToCollectionModal = useCallback(() => {
    if (!activeProductTokenRef.current) {
      return;
    }
    setSavedProductToast(null);
    isCollectionToastOpenRef.current = false;
    setIsCollectionToastOpen(false);
    isPickerOpenRef.current = false;
    setIsPickerOpen(false);
    setIsAddToCollectionModalOpen(true);
  }, []);

  const closeAddToCollectionModal = useCallback(() => {
    setIsAddToCollectionModalOpen(false);
  }, []);

  const handleCollectionToastClose = useCallback(() => {
    isCollectionToastOpenRef.current = false;
    setIsCollectionToastOpen(false);
  }, []);

  const handlePickerExitComplete = useCallback(() => {
    setIsPickerRendered(false);
    const pendingAllFavoritesImage = pendingAllFavoritesImageRef.current;
    if (!pendingAllFavoritesImage) {
      return;
    }
    pendingAllFavoritesImageRef.current = null;
    setCollections((currentCollections) =>
      updateAllFavoritesCollectionImage(
        currentCollections,
        pendingAllFavoritesImage.imageUrl
      )
    );
  }, []);

  const handleCollectionImageTransitionComplete = useCallback(
    (collectionId: string, transitionId: number) => {
      setCollectionImageTransitions((currentImageTransitions) => {
        if (currentImageTransitions[collectionId]?.id !== transitionId) {
          return currentImageTransitions;
        }
        const nextImageTransitions = { ...currentImageTransitions };
        delete nextImageTransitions[collectionId];
        return nextImageTransitions;
      });
    },
    []
  );

  const handleFavoriteClick = useCallback(
    (
      productToken: string,
      isFavorited: boolean,
      nextAnchorElement: HTMLElement,
      productImageUrl?: string
    ) => {
      if (!nextAnchorElement) {
        return;
      }
      if (productImageUrl) {
        productImageOverrides.current[productToken] = productImageUrl;
      }
      // Second click on the same product's heart dismisses an open surface.
      const isPopoverVariant =
        saveFlowVariant === SAVE_FLOW_POPOVER ||
        saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED ||
        saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED_R2 ||
        saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED_R3 ||
        saveFlowVariant === SAVE_FLOW_POPOVER_R2;
      if (
        isPopoverVariant &&
        isPickerOpenRef.current &&
        activeProductTokenRef.current === productToken
      ) {
        handlePickerClose();
        return;
      }

      if (!isFavorited) {
        pendingAllFavoritesImageRef.current = null;
        setCollections((currentCollections) =>
          updateAllFavoritesCollectionImage(
            currentCollections,
            resolveImage(productToken)
          )
        );
        setProductTokensByCollection((currentProductTokensByCollection) =>
          addProductToCollection(
            currentProductTokensByCollection,
            ALL_FAVORITES_COLLECTION_ID,
            productToken
          )
        );
      }
      // Warm the collection thumbnails in the background, but do not gate the
      // surface open on it — decode() can hang for some CDN images and would
      // otherwise prevent it from ever opening.
      void preloadCollectionImages();
      activeProductTokenRef.current = productToken;
      setActiveProductToken(productToken);
      setAnchorElement(nextAnchorElement);

      // Expanding toast: save one-click, open the bottom-pinned collapsed toast
      // that expands into the collections list on demand.
      if (
        saveFlowVariant === SAVE_FLOW_EXPANDING_TOAST ||
        saveFlowVariant === SAVE_FLOW_EXPANDING_TOAST_R2 ||
        saveFlowVariant === SAVE_FLOW_TOAST_BLACK ||
        saveFlowVariant === SAVE_FLOW_TOAST_LIGHT ||
        saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM ||
        saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM_LIGHT_EXPAND
      ) {
        setSavedProductToast(null);
        isPickerOpenRef.current = false;
        setIsPickerOpen(false);
        // Re-saving an already-saved product opens straight to the expanded
        // drawer so you can organize or remove it.
        setCollectionToastInitialExpanded(isFavorited);
        isCollectionToastOpenRef.current = true;
        setIsCollectionToastOpen(true);
        return;
      }

      // Confirmation toast: first save is one-click and shows a lightweight
      // confirmation whose action opens the modal as a deliberate next step.
      // Clicking the heart again on an already-saved product jumps straight to
      // the modal so you can organize it.
      if (saveFlowVariant === SAVE_FLOW_CONFIRMATION_TOAST) {
        isPickerOpenRef.current = false;
        setIsPickerOpen(false);
        isCollectionToastOpenRef.current = false;
        setIsCollectionToastOpen(false);
        if (isFavorited) {
          setSavedProductToast(null);
          setIsAddToCollectionModalOpen(true);
          return;
        }
        const savedImageUrl = resolveImage(productToken);
        if (savedImageUrl) {
          setSavedProductToast({
            kind: "saved-to-favorites",
            imageUrl: savedImageUrl,
          });
        }
        return;
      }

      // Popover (default): open the collections picker anchored to the heart.
      // Re-opening an already-saved product jumps straight to the expanded list
      // (condensed variant) so you can organize or remove it.
      setSavedProductToast(null);
      isCollectionToastOpenRef.current = false;
      setIsCollectionToastOpen(false);
      setPickerInitialExpanded(isFavorited);
      isPickerOpenRef.current = true;
      setIsPickerRendered(true);
      setIsPickerOpen(true);
    },
    [handlePickerClose, resolveImage, saveFlowVariant]
  );

  const selectedCollectionIds = activeProductToken
    ? collections
        .filter((collection) =>
          (productTokensByCollection[collection.id] ?? []).includes(
            activeProductToken
          )
        )
        .map((collection) => collection.id)
    : [];

  const handleToggleCollection = (collectionId: string) => {
    if (!activeProductToken) {
      return;
    }

    const removesProductFromAllFavorites =
      collectionId === ALL_FAVORITES_COLLECTION_ID &&
      selectedCollectionIds.includes(ALL_FAVORITES_COLLECTION_ID);
    if (removesProductFromAllFavorites) {
      const remainingFavoriteProductTokens = (
        productTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? []
      ).filter((productToken) => productToken !== activeProductToken);
      const latestFavoriteProductToken =
        remainingFavoriteProductTokens[remainingFavoriteProductTokens.length - 1];
      pendingAllFavoritesImageRef.current = {
        imageUrl: latestFavoriteProductToken
          ? getProductImageUrl(latestFavoriteProductToken)
          : INITIAL_ALL_FAVORITES_IMAGE_URL,
      };
    }

    const isRemovingProduct = selectedCollectionIds.includes(collectionId);

    const addsProductToAllFavorites =
      !isRemovingProduct &&
      (collectionId === ALL_FAVORITES_COLLECTION_ID ||
        !selectedCollectionIds.includes(ALL_FAVORITES_COLLECTION_ID));
    if (addsProductToAllFavorites) {
      pendingAllFavoritesImageRef.current = null;
      setCollections((currentCollections) =>
        updateAllFavoritesCollectionImage(
          currentCollections,
          resolveImage(activeProductToken)
        )
      );
    }

    setProductTokensByCollection((currentProductTokensByCollection) => {
      if (removesProductFromAllFavorites) {
        const nextProductTokensByCollection = {
          ...currentProductTokensByCollection,
        };
        selectedCollectionIds.forEach((selectedCollectionId) => {
          nextProductTokensByCollection[selectedCollectionId] = (
            currentProductTokensByCollection[selectedCollectionId] ?? []
          ).filter((productToken) => productToken !== activeProductToken);
        });
        return nextProductTokensByCollection;
      }

      if (isRemovingProduct) {
        return removeProductFromCollection(
          currentProductTokensByCollection,
          collectionId,
          activeProductToken
        );
      }

      const productTokensWithSelectedCollection = addProductToCollection(
        currentProductTokensByCollection,
        collectionId,
        activeProductToken
      );
      return collectionId !== ALL_FAVORITES_COLLECTION_ID &&
        !selectedCollectionIds.includes(ALL_FAVORITES_COLLECTION_ID)
        ? addProductToCollection(
            productTokensWithSelectedCollection,
            ALL_FAVORITES_COLLECTION_ID,
            activeProductToken
          )
        : productTokensWithSelectedCollection;
    });
  };

  const handleCreateCollection = (collectionName: string) => {
    if (!activeProductToken) {
      return;
    }

    const collectionId = `new-collection-${nextCollectionNumber.current}`;
    const activeProductImageUrl = resolveImage(activeProductToken);
    if (!activeProductImageUrl) {
      return;
    }
    nextCollectionNumber.current += 1;
    setCollections((currentCollections) => {
      const newCollectionIndex =
        currentCollections.findIndex(
          (collection) => collection.id === ALL_FAVORITES_COLLECTION_ID
        ) + 1;

      return [
        ...currentCollections.slice(0, newCollectionIndex),
        {
          id: collectionId,
          name: collectionName,
        },
        ...currentCollections.slice(newCollectionIndex),
      ];
    });
    setProductTokensByCollection((currentProductTokensByCollection) => {
      const productTokensWithAllFavorites = (
        currentProductTokensByCollection[ALL_FAVORITES_COLLECTION_ID] ?? []
      ).includes(activeProductToken)
        ? currentProductTokensByCollection
        : addProductToCollection(
            currentProductTokensByCollection,
            ALL_FAVORITES_COLLECTION_ID,
            activeProductToken
          );
      return addProductToCollection(
        productTokensWithAllFavorites,
        collectionId,
        activeProductToken
      );
    });
    setSavedProductToast({
      kind: "created-collection",
      collectionName,
      imageUrl: activeProductImageUrl,
    });
    handlePickerClose();
    handleCollectionToastClose();
  };

  const activeProductImageUrl = activeProductToken
    ? resolveImage(activeProductToken)
    : undefined;
  const activeProduct =
    activeProductToken && activeProductImageUrl
      ? { token: activeProductToken, imageUrl: activeProductImageUrl }
      : undefined;
  const collectionsWithCurrentImages = collections.map((collection) => {
    // Collection previews reflect the FIRST image saved in the collection (its
    // seed image, or the earliest product added) — never the just-saved
    // product. The "Saved" (All favorites) row still uses the saved product's
    // image (see the picker header + updateAllFavoritesCollectionImage).
    const imageUrl =
      collection.id === ALL_FAVORITES_COLLECTION_ID
        ? collection.imageUrl
        : (collection.imageUrl ??
          getFirstCollectionImageUrl(collection.id, productTokensByCollection));
    const imageTransition = collectionImageTransitions[collection.id];
    return {
      ...collection,
      ...(imageTransition ? { imageTransition } : {}),
      imageUrl,
    };
  });
  const visibleCollections =
    extraCollectionsState === EXTRA_COLLECTIONS_VISIBLE
      ? collectionsWithCurrentImages
      : collectionsWithCurrentImages.filter(
          (collection) => !SEEDED_EXTRA_COLLECTION_IDS.has(collection.id)
        );
  const allFavoritesCollectionName =
    visibleCollections.find(
      (collection) => collection.id === ALL_FAVORITES_COLLECTION_ID
    )?.name ?? "saved products";
  const pickerAnchorLocation: PopoverAnchorLocation =
    popoverAnchorLocation === POPOVER_ANCHOR_HEART ? "heart" : "product-card";

  const savedCount =
    productTokensByCollection[ALL_FAVORITES_COLLECTION_ID]?.length ?? 0;
  const organizingProductToken =
    (isPickerOpen || isCollectionToastOpen) && activeProductToken
      ? activeProductToken
      : null;

  const isSaved = useCallback(
    (productToken: string) =>
      productTokensByCollection[ALL_FAVORITES_COLLECTION_ID]?.includes(
        productToken
      ) ?? false,
    [productTokensByCollection]
  );

  // Popover condensed R2: re-opening an already-saved product opens to a
  // summary of the collections it's in (with an "Add to collection" button that
  // expands to the full list), instead of jumping straight to the full list.
  // R3 shares the R2 treatment and adds a confirm-with-"Save" step.
  const isCondensedR3Variant = saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED_R3;
  const isCondensedR2Variant =
    saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED_R2 || isCondensedR3Variant;
  const pickerSavedSummary = isCondensedR2Variant && pickerInitialExpanded;
  const getPickerStartExpanded = (): boolean => {
    if (isCondensedR2Variant) {
      return false;
    }
    if (saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED) {
      return pickerInitialExpanded;
    }
    return true;
  };

  const popoverElement =
    isPickerRendered && activeProduct && anchorElement ? (
      <FavoriteCollectionPicker
        anchorElement={anchorElement}
        anchorLocation={pickerAnchorLocation}
        closeOnCollectionSelect={
          closeAfterSelectionState === CLOSE_AFTER_SELECTION_ON &&
          !isCondensedR3Variant
        }
        confirmWithSave={isCondensedR3Variant}
        collections={visibleCollections}
        condensed={
          saveFlowVariant === SAVE_FLOW_POPOVER_CONDENSED || isCondensedR2Variant
        }
        r2={isCondensedR2Variant}
        savedSummary={pickerSavedSummary}
        popoverR2={saveFlowVariant === SAVE_FLOW_POPOVER_R2}
        startExpanded={getPickerStartExpanded()}
        key={activeProduct.token}
        onClose={handlePickerClose}
        onCollectionImageTransitionComplete={
          handleCollectionImageTransitionComplete
        }
        onCreateCollection={handleCreateCollection}
        onExitComplete={handlePickerExitComplete}
        onToggleCollection={handleToggleCollection}
        open={isPickerOpen}
        productImageUrl={activeProduct.imageUrl}
        selectedCollectionIds={selectedCollectionIds}
      />
    ) : null;

  const renderSavedToast = () => {
    if (!savedProductToast) {
      return null;
    }
    if (savedProductToast.kind === "created-collection") {
      return (
        <FavoriteSavedToast
          collectionName={savedProductToast.collectionName}
          imageUrl={savedProductToast.imageUrl}
          key={`created-${savedProductToast.collectionName}`}
          kind="created-collection"
          onAction={() => setSavedProductToast(null)}
          onClose={() => setSavedProductToast(null)}
        />
      );
    }
    return (
      <FavoriteSavedToast
        imageUrl={savedProductToast.imageUrl}
        key="saved-to-favorites"
        kind="saved-to-favorites"
        onAction={openAddToCollectionModal}
        onClose={() => setSavedProductToast(null)}
      />
    );
  };

  const toastElement = (
    <>
      <AnimatePresence>{renderSavedToast()}</AnimatePresence>
      <AddToCollectionModal
        collections={visibleCollections}
        onClose={closeAddToCollectionModal}
        onSave={closeAddToCollectionModal}
        onToggleCollection={handleToggleCollection}
        open={isAddToCollectionModalOpen}
        selectedCollectionIds={selectedCollectionIds}
      />
    </>
  );

  const collectionToastElement = (
    <AnimatePresence>
      {isCollectionToastOpen && activeProduct ? (
        <FavoriteCollectionToast
          collapsedLayout={
            saveFlowVariant === SAVE_FLOW_TOAST_LIGHT ? "stacked" : "inline"
          }
          collections={visibleCollections}
          closeOnTimer={saveFlowVariant === SAVE_FLOW_EXPANDING_TOAST}
          confirmWithSave={saveFlowVariant === SAVE_FLOW_EXPANDING_TOAST_R2}
          initialExpanded={collectionToastInitialExpanded}
          key={activeProduct.token}
          onClose={handleCollectionToastClose}
          onCreateCollection={handleCreateCollection}
          onToggleCollection={handleToggleCollection}
          placement={
            saveFlowVariant === SAVE_FLOW_TOAST_BLACK ||
            saveFlowVariant === SAVE_FLOW_TOAST_LIGHT
              ? "top-right"
              : "bottom-center"
          }
          productImageUrl={activeProduct.imageUrl}
          savedDestinationName={
            saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM ||
            saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM_LIGHT_EXPAND
              ? allFavoritesCollectionName
              : undefined
          }
          selectedCollectionIds={selectedCollectionIds}
          theme={
            saveFlowVariant === SAVE_FLOW_TOAST_BLACK ||
            saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM ||
            saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM_LIGHT_EXPAND
              ? "dark"
              : "light"
          }
          expandedTheme={
            saveFlowVariant === SAVE_FLOW_TOAST_DARK_BOTTOM_LIGHT_EXPAND
              ? "light"
              : undefined
          }
        />
      ) : null}
    </AnimatePresence>
  );

  return {
    handleFavoriteClick,
    isSaved,
    organizingProductToken,
    savedCount,
    extraCollectionsState,
    setExtraCollectionsState,
    popoverElement,
    toastElement,
    collectionToastElement,
  };
};
